import { SUITS } from '../game/types';
import { syndicateById } from '../game/syndicates';

interface PlayerPanelProps {
  side: 'me' | 'opponent';
  name: string;
  snatchPoints: number;
  resources: Record<string, number>;
  avatarId?: number | null;
  syndicate?: string | null;
}

function avatarInitial(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const inner = trimmed.match(/^BOT\s*\(([^)]+)\)$/i)?.[1] ?? trimmed.replace(/^\[?BOT\]?$/i, 'B');
  return inner.charAt(0).toUpperCase();
}

export function PlayerPanel({ side, name, snatchPoints, resources, avatarId, syndicate }: PlayerPanelProps) {
  const syn = syndicateById(syndicate);
  const hasAvatar = avatarId !== null && avatarId !== undefined && Number.isInteger(avatarId);

  // Show a pill for every suit with a non-zero value, regardless of whether the
  // player drafted that suit — capturing an opponent's card credits its suit
  // here too. Suit order follows the canonical SUITS tuple so the pill row is
  // stable across renders; any suit present in resources but not in SUITS
  // (shouldn't happen today) is appended at the end.
  const knownEntries = SUITS
    .filter(s => (resources[s] ?? 0) > 0)
    .map(s => [s as string, resources[s] as number] as const);
  const unknownEntries = Object.entries(resources)
    .filter(([s, v]) => v > 0 && !(SUITS as readonly string[]).includes(s));
  const entries = [...knownEntries, ...unknownEntries];

  return (
    <div className={`player-panel player-panel-${side}`}>
      <div className="player-panel-avatar">
        {hasAvatar ? (
          <div
            className={`avatar-sprite avatar-sprite-${avatarId}`}
            role="img"
            aria-label={`avatar ${avatarId}`}
          />
        ) : (
          <span className="player-panel-avatar-fallback">{avatarInitial(name)}</span>
        )}
      </div>
      <div className="player-panel-info">
        <div className="player-panel-header">
          <span className="player-panel-name">{name}</span>
          <span className="player-panel-sp">SP {snatchPoints}</span>
        </div>
        {syn && (
          <div className="player-panel-syndicate" title={syn.bonus}>★ {syn.name}</div>
        )}
        <div className="player-panel-resources">
          {entries.length === 0 ? (
            <span className="player-panel-resources-empty">—</span>
          ) : (
            entries.map(([suit, val]) => (
              <span key={suit} className="res-pill player-panel-pill" title={suit}>
                <span
                  className={`suit-icon suit-icon-${suit.replace(/\s+/g, '-')}`}
                  style={{ width: '24px', height: '24px' }}
                />
                <span className="player-panel-pill-val">{val}</span>
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
